import type { BadgeVariant } from '@/components/Badge';
import type { SalaryRecord, SalaryRecordStatus } from '@/mocks/db/types';
import type { SalaryAndWagesSelector } from '@/routes/_staff/salary-and-wages/-utils/i18n';

/**
 * 帳票が取るデータのキー。テーブルの行の型とカラムをこれが決める。
 * `wageLedgerBook` だけは 1 行が帳票そのもの（賃金台帳 1 冊）で、
 * 中のレコードは行を開いた先の台帳詳細に出る。
 */
export type ReportGrain = 'employeePeriod' | 'wageLedgerBook' | 'employeeTaxYear' | 'officeMonth' | 'officeYear';
export type ReportCategory = 'individual' | 'organization';

/**
 * `z.enum` にそのまま渡せるよう readonly タプルで持つ。ここから型を導出することで
 * `-zod-schema.ts` 側の `as` キャストが不要になる。
 */
export const STATUTORY_REPORT_IDS = ['payslip', 'bonusPayslip', 'wageLedger', 'withholdingSlip', 'retirementWithholdingSlip', 'incomeTaxStatement', 'residentTaxStatement', 'statutoryReportSummary', 'salaryPaymentReport', 'laborInsuranceDeclaration', 'standardRemunerationNotification'] as const;
export type StatutoryReportId = (typeof STATUTORY_REPORT_IDS)[number];
export interface StatutoryReport {
  id: StatutoryReportId;
  category: ReportCategory;
  grain: ReportGrain;
  /** 集計前のレコード絞り込み。未指定なら全レコードが対象 */
  recordFilter?: (record: SalaryRecord) => boolean;
  /** officeMonth グレインの徴収税額の出典となる明細行の itemCode */
  lineItemCode?: string;
  /** officeYear グレインのグループ化に使うレコードのフィールド */
  periodBasis?: 'taxYear' | 'fiscalYear';
}
export const STATUTORY_REPORTS: StatutoryReport[] = [{
  id: 'payslip',
  category: 'individual',
  grain: 'employeePeriod',
  recordFilter: record => record.runType === 'regular'
}, {
  id: 'bonusPayslip',
  category: 'individual',
  grain: 'employeePeriod',
  recordFilter: record => record.runType === 'bonus'
},
// 賃金台帳は事業場ごとに労働者各人別で調製する帳票（労基法108条）。一覧は
// 「2026年 賃金台帳 / 山田太郎」のように本人の台帳 1 冊を 1 行として並べ、
// 中の賃金計算期間は個人詳細の賃金台帳タブで見る
{
  id: 'wageLedger',
  category: 'individual',
  grain: 'wageLedgerBook'
}, {
  id: 'withholdingSlip',
  category: 'individual',
  grain: 'employeeTaxYear'
}, {
  id: 'retirementWithholdingSlip',
  category: 'individual',
  grain: 'employeeTaxYear',
  // 退職所得は退職精算の run で支払われた分だけ。`terminationDate` で絞ると
  // 退職者の通常給与・賞与（給与所得）まで退職所得へ混入する
  recordFilter: record => record.runType === 'termination'
}, {
  id: 'incomeTaxStatement',
  category: 'organization',
  grain: 'officeMonth',
  lineItemCode: 'D-WHT'
}, {
  id: 'residentTaxStatement',
  category: 'organization',
  grain: 'officeMonth',
  lineItemCode: 'D-RES'
}, {
  id: 'statutoryReportSummary',
  category: 'organization',
  grain: 'officeYear',
  periodBasis: 'taxYear'
}, {
  id: 'salaryPaymentReport',
  category: 'organization',
  grain: 'officeYear',
  periodBasis: 'taxYear'
}, {
  id: 'laborInsuranceDeclaration',
  category: 'organization',
  grain: 'officeYear',
  periodBasis: 'fiscalYear'
}, {
  id: 'standardRemunerationNotification',
  category: 'organization',
  grain: 'officeYear',
  periodBasis: 'fiscalYear'
}];
export const DEFAULT_REPORT_ID = 'payslip' satisfies StatutoryReportId;
export function findReport(id: StatutoryReportId): StatutoryReport {
  const report = STATUTORY_REPORTS.find(entry => entry.id === id);
  // STATUTORY_REPORTS は StatutoryReportId を網羅する（-constants.test.ts で担保）
  return report ?? STATUTORY_REPORTS[0];
}

/**
 * サイドバーの構造: カテゴリ見出し > 帳票。
 * 帳票の並び順はデータキー（`grain`）順だが、キー自体は見出しとして出さない。
 */
export interface SidebarSection {
  category: ReportCategory;
  reportIds: StatutoryReportId[];
}
export const SIDEBAR_SECTIONS: SidebarSection[] = [{
  category: 'individual',
  reportIds: ['payslip', 'bonusPayslip',
  // 賃金台帳は他の帳票の原簿にあたるため、明細のあと・源泉徴収票の前に置く
  'wageLedger', 'withholdingSlip', 'retirementWithholdingSlip']
}, {
  category: 'organization',
  reportIds: ['incomeTaxStatement', 'residentTaxStatement', 'statutoryReportSummary', 'salaryPaymentReport', 'laborInsuranceDeclaration', 'standardRemunerationNotification']
}];
export const statutoryReportSelectors = {
  bonusPayslip: $ => $.reports.names.bonusPayslip,
  incomeTaxStatement: $ => $.reports.names.incomeTaxStatement,
  laborInsuranceDeclaration: $ => $.reports.names.laborInsuranceDeclaration,
  payslip: $ => $.reports.names.payslip,
  residentTaxStatement: $ => $.reports.names.residentTaxStatement,
  retirementWithholdingSlip: $ => $.reports.names.retirementWithholdingSlip,
  salaryPaymentReport: $ => $.reports.names.salaryPaymentReport,
  standardRemunerationNotification: $ => $.reports.names.standardRemunerationNotification,
  statutoryReportSummary: $ => $.reports.names.statutoryReportSummary,
  wageLedger: $ => $.reports.names.wageLedger,
  withholdingSlip: $ => $.reports.names.withholdingSlip
} satisfies Record<StatutoryReportId, SalaryAndWagesSelector>;
export const reportCategorySelectors = {
  individual: $ => $.reports.categories.individual,
  organization: $ => $.reports.categories.organization
} satisfies Record<ReportCategory, SalaryAndWagesSelector>;

/** SalaryRecordStatus → バッジ配色。 */
export const statusBadgeVariant: Record<SalaryRecordStatus, BadgeVariant> = {
  approving: 'mutedWarning',
  calculated: 'secondaryMuted',
  calculating: 'mutedWarning',
  cancelled: 'errorMuted',
  delivered: 'complementaryMuted',
  draft: 'neutralMuted',
  finalized: 'primaryMuted',
  paid: 'mutedSuccess'
};